import { api } from '@/lib/api';

/**
 * 입소자에게 발급된 두 서비스의 로그인 정보.
 *
 * 보이스웰니스 비밀번호가 없는 이유: 서버에 bcrypt 해시만 저장되어 원문이 어디에도
 * 없다. 되찾는 길이 없고 재발급만 가능하다. BGT 쪽은 환자번호가 평문, 생년월일이
 * 가역 암호라 언제든 다시 볼 수 있다.
 */
export interface ResidentCredentials {
  bgtPatientCode: string | null;
  bgtBirthDate: string | null;
  voiceLoginId: string | null;
  /** 항상 false. 화면이 '다시 보기'를 제안하지 않도록 서버가 명시해 준다 */
  voicePasswordRetrievable: boolean;
}

/** 계정을 새로 만들거나 비밀번호를 재발급한 직후에만 받는 응답 */
export interface IssuedCredentials extends ResidentCredentials {
  /** 이 응답에서만 볼 수 있다. 화면을 닫으면 다시 꺼낼 수 없다 */
  voiceTemporaryPassword: string | null;
}

export async function getResidentCredentials(residentId: string): Promise<ResidentCredentials> {
  const res = await api.get<ResidentCredentials>(`/residents/${residentId}/credentials`);
  return res.data;
}

export async function issueVoiceAccount(residentId: string): Promise<IssuedCredentials> {
  const res = await api.post<IssuedCredentials>(`/residents/${residentId}/voice-account`);
  return res.data;
}

export async function resetVoicePassword(residentId: string): Promise<IssuedCredentials> {
  const res = await api.post<IssuedCredentials>(
    `/residents/${residentId}/voice-account/reset-password`,
  );
  return res.data;
}

/**
 * 발급하면 어떤 아이디가 나올지 — 등록 폼에서 저장 전에 보여준다.
 * loginId가 null이면 근거(환자번호·호실)가 없다는 뜻이고, 직접 입력해야 한다.
 */
export async function previewVoiceLoginId(params: {
  patientCode?: string | null;
  roomNumber?: string | null;
}): Promise<{ loginId: string | null }> {
  const res = await api.get<{ loginId: string | null }>('/residents/voice-login-id/preview', {
    params: {
      patientCode: params.patientCode || undefined,
      roomNumber: params.roomNumber || undefined,
    },
  });
  return res.data;
}

export interface CreateBgtUserInput {
  patientCode: string;
  birthDate: string;
  name?: string;
}

export interface BgtUser {
  id: string;
  name: string | null;
  patientCode: string | null;
  birthDate: string | null;
  age: number | null;
  alreadyMapped: boolean;
}

export async function createBgtUser(input: CreateBgtUserInput): Promise<BgtUser> {
  const res = await api.post<BgtUser>('/bgt-users', input);
  return res.data;
}

export async function updateBgtUser(
  id: string,
  input: Partial<CreateBgtUserInput>,
): Promise<BgtUser> {
  const res = await api.put<BgtUser>(`/bgt-users/${id}`, input);
  return res.data;
}
